import React, { useCallback, useEffect } from 'react';
import { captureException } from '@sentry/browser';
import PropTypes from 'prop-types';
import {
  matchPath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import chronosApi from '@chronos/api-client/chronosApi';
import useTimer from '@chronos/ui-toolkit/hooks/useTimer';
// import useKeyboardShortcut from '@chronos/ui-toolkit/hooks/useKeyboardShortcut';
import eventEmitter from '@chronos/utils/eventEmitter';
import { setIsInitialized, saveLiveWorklog } from '@chronos/slices/boardSlice';
import TrackingBar from '@chronos/components/TrackingBar';
import { useGetBoardsQuery } from '@chronos/slices/boardsApi';
import { useBoardContext } from '@chronos/ui-toolkit/contexts';
import { selectProfile } from '../../slices/appSlice';
import { openModal } from '../../slices/modalSlice';

const TrackingBarContainer = ({
  liveWorklog,
  hasMultipleTimers,
  setTrackerExpanded,
  isTrackerExpanded,
  isExpandCollapseVisible,
  fetchOutTrelloCards,
  cardsEntities,
}) => {
  let { cardId: routeCardId } = useParams();
  const { memberId, boardMemberSettings } = useBoardContext();
  const { boardId: routeBoardId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const profile = useSelector(selectProfile);
  const {
    data: { entities: boardsEntities },
  } = useGetBoardsQuery({
    memberId,
  });
  const liveWorklogBoard = boardsEntities[liveWorklog.boardId];

  const matches = matchPath('/:boardId/cards/:cardId', location.pathname);
  if (!routeCardId && matches?.params.cardId) {
    routeCardId = matches.params.cardId;
  }

  useEffect(() => {
    if (!cardsEntities[liveWorklog.cardId]) {
      fetchOutTrelloCards([liveWorklog.cardId]);
    }
  }, [liveWorklog, cardsEntities]);

  const [{ state: timerState }, { syncTimer }] = useTimer(0);

  useEffect(() => {
    syncTimer(liveWorklog);
  }, [liveWorklog]);

  const getSeconds = (startTime, endTime) =>
    Number.parseInt((endTime - startTime) / 1000, 10);

  const openWorklogReviewFormModal = useCallback(
    (meta) => {
      dispatch(
        openModal({
          name: 'WorklogFormModal',
          meta: {
            title: 'Worklog Review',
            organizationId: liveWorklogBoard?.idOrganization,
            cardId: liveWorklog.cardId,
            boardId: liveWorklog.boardId,
            memberId: liveWorklog.memberId,
            ...meta,
          },
        })
      );
    },
    [timerState, dispatch]
  );

  const setLiveWorklog = useCallback(
    (ts) => {
      const { cardId } = ts;
      if (!cardId || !ts.startTime || !ts.timerStatus) {
        throw new Error("Can't play/pause timer without cardId or startTime");
      }

      (async () => {
        // eventEmitter.emit('syncTimer', { cardId, state: ts });
        dispatch(
          saveLiveWorklog({
            cardId,
            boardId: ts.boardId,
            state: {
              organizationId: liveWorklogBoard.idOrganization,
              ...ts,
            },
          })
        );
      })();
    },
    [liveWorklogBoard.idOrganization]
  );

  const playPauseTimer = useCallback((ts, nextTimerStatus = undefined) => {
    const { cardId } = ts;
    if (!cardId) {
      throw new Error("Can't play/pause timer without cardId");
    }

    const now = +new Date();
    nextTimerStatus =
      nextTimerStatus ?? (ts.timerStatus === 'run' ? 'pause' : 'run');

    syncTimer({
      ...ts,
      timerStatus: nextTimerStatus,
      ...(nextTimerStatus === 'pause'
        ? {
            pauseStartTime: now,
          }
        : {}),
    });

    if (ts.timerStatus === nextTimerStatus) {
      // timer is already in this status
      return;
    }

    const startTime = ts.startTime || now;
    let pauseTrackedSeconds = ts.pauseTrackedSeconds || 0;
    let pauseStartTime = ts.pauseStartTime || null;
    if (nextTimerStatus === 'run' && pauseStartTime) {
      pauseTrackedSeconds += getSeconds(pauseStartTime, now);
      pauseStartTime = null;
    }
    if (nextTimerStatus === 'pause') {
      pauseStartTime = now;
    }

    const newTimerState = {
      ...ts,
      memberId: profile.id,
      startTime,
      timerStatus: nextTimerStatus,
      pauseTrackedSeconds,
      pauseStartTime,
      delay: nextTimerStatus === 'run' ? 1000 : null,
    };

    setLiveWorklog(newTimerState);
  }, []);

  const stopTimer = useCallback(
    async (ts) => {
      const { cardId } = ts;
      if (!cardId || !ts.startTime || !ts.timerStatus) {
        throw new Error("Can't play/pause timer without cardId or startTime");
      }

      syncTimer({
        ...timerState,
        timerStatus: 'stop',
      });

      const now = +new Date();
      let pauseTrackedSeconds = ts.pauseTrackedSeconds || 0;
      if (ts.timerStatus === 'pause') {
        pauseTrackedSeconds += getSeconds(ts.pauseStartTime, now);
      }

      try {
        const body = {
          cardId,
          boardId: ts.boardId,
          // In case if user change a card it needs to delete timerState on the backend
          // timerCardId: currentCardId,
          organizationId: liveWorklogBoard.idOrganization,
          timerCardId: cardId,
          seconds: getSeconds(ts.startTime, now) - pauseTrackedSeconds,
          screenshotsPeriod: 600,
          activity: [],
          startedAt: new Date(ts.startTime).toISOString(),
          comment: ts.comment || '',
        };
        eventEmitter.emit('saveWorklog', { body });
        await chronosApi.saveWorklog({ body });
        eventEmitter.emit('saveWorklogFinish', { body });
      } catch (e) {
        console.error(e);
        captureException(e);
      }
    },
    [liveWorklogBoard.idOrganization]
  );

  const handlePlayPauseTimer = useCallback(() => {
    if (timerState.timerStatus === 'stop') {
      // first run timer
      timerState.startTime = Date.now();
    }
    if (
      timerState.timerStatus === 'stop' &&
      boardMemberSettings.showStartReview
    ) {
      openWorklogReviewFormModal({
        worklog: { seconds: 0 },
        mode: 'startReview',
      });
    } else {
      timerState.trackingWorklogComment = liveWorklog.trackingWorklogComment;
      playPauseTimer(timerState);
    }
  }, [
    timerState,
    playPauseTimer,
    liveWorklog.trackingWorklogComment,
    boardMemberSettings.showStartReview,
  ]);

  useEffect(() => {
    const handlePlayTimer = (lw) => {
      if (timerState.cardId === lw.cardId) {
        playPauseTimer(timerState, 'run');
      }
    };
    const handlePauseTimer = (lw) => {
      playPauseTimer(lw, 'pause');
    };
    eventEmitter.on('timer:play', handlePlayTimer);
    eventEmitter.on('timer:pause', handlePauseTimer);
    return () => {
      eventEmitter.off('timer:play', handlePlayTimer);
      eventEmitter.off('timer:pause', handlePauseTimer);
    };
  }, [handlePlayPauseTimer, timerState]);

  const handleStopTimer = useCallback(async () => {
    const { trackingWorklogComment } = liveWorklog;
    if (boardMemberSettings.showStopReview !== false) {
      const worklog = {
        seconds: timerState.timer,
        startedAt: new Date(timerState.startTime).toISOString(),
        comment: trackingWorklogComment,
      };
      timerState.trackingWorklogComment = trackingWorklogComment;
      playPauseTimer(timerState, 'pause');
      openWorklogReviewFormModal({
        worklog,
        mode: 'stopReview',
      });
    } else {
      timerState.comment = trackingWorklogComment;
      await stopTimer(timerState);
    }
  }, [
    timerState,
    liveWorklog.trackingWorklogComment,
    boardMemberSettings.showStopReview,
  ]);

  const handleWorklogCommentSubmit = useCallback(
    async (comment) => {
      const cardTimerState = {
        ...timerState,
        trackingWorklogComment: comment,
      };
      setLiveWorklog(cardTimerState);
    },
    [timerState, setLiveWorklog]
  );

  // ' ' - space key
  // TODO: fix it
  // useKeyboardShortcut([' '], () => handlePlayPauseTimer());

  const handleClickCardName = useCallback(
    async (card) => {
      if (routeCardId === card.id) {
        return;
      }

      const boardIsChanged = routeBoardId !== card.idBoard;
      if (boardIsChanged) {
        dispatch(setIsInitialized(false));
      }
      const pathname = `/${card.idBoard}/cards/${card.id}`;
      const backgroundLocation = location.state?.backgroundLocation || location;
      const state = boardIsChanged ? null : { backgroundLocation };
      navigate(pathname, { state });
    },
    [routeCardId, routeBoardId]
  );

  const isExternal = routeBoardId !== liveWorklog.boardId;
  const isRunning = ({ timerStatus }) => ['run', 'pause'].includes(timerStatus);
  const showButtons =
    liveWorklog.boardId === routeBoardId ||
    liveWorklogBoard.idOrganization !== undefined;

  if (isRunning(timerState) || routeCardId) {
    return (
      <>
        <TrackingBar
          timerState={timerState}
          onStopTimer={handleStopTimer}
          onPlayPause={handlePlayPauseTimer}
          onClickTrackingCardname={handleClickCardName}
          onClickEstimateModal={() => {}}
          card={cardsEntities[liveWorklog.cardId]}
          position="bottom"
          mode="pwa"
          size="large"
          /* size="medium" */
          showEstimates={false}
          estimates={[]}
          isExternal={isExternal}
          hasMultipleTimers={hasMultipleTimers}
          setTrackerExpanded={setTrackerExpanded}
          isTrackerExpanded={isTrackerExpanded}
          isExpandCollapseVisible={isExpandCollapseVisible}
          showButtons={showButtons}
          onSaveComment={handleWorklogCommentSubmit}
        />
      </>
    );
  }

  return <div />;
};

TrackingBarContainer.defaultProps = {};

TrackingBarContainer.propTypes = {
  liveWorklog: PropTypes.shape({
    memberId: PropTypes.string,
    boardId: PropTypes.string,
    cardId: PropTypes.string,
    startTime: PropTypes.number,
    pauseStartTime: PropTypes.number,
    timerStatus: PropTypes.string,
    pauseTrackedSeconds: PropTypes.number,
    trackingWorklogComment: PropTypes.string,
  }).isRequired,
  isExpandCollapseVisible: PropTypes.bool,
  isTrackerExpanded: PropTypes.bool,
  hasMultipleTimers: PropTypes.bool,
  setTrackerExpanded: PropTypes.func.isRequired,
  fetchOutTrelloCards: PropTypes.func,
  cardsEntities: PropTypes.objectOf(PropTypes.object),
};

export default TrackingBarContainer;
